import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { ProductCard } from '../components/products/ProductCard';
import { FiltersSidebar } from '../components/products/FiltersSidebar';
import { ActiveFilterChips } from '../components/products/ActiveFilterChips';
import { EmptyState } from '../components/products/EmptyState';
import { products, manufacturers, productTypes, productNote } from '../data/products';

const ProductsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = searchQuery === '' || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesManufacturer = selectedManufacturers.length === 0 || 
        selectedManufacturers.includes(product.manufacturer);
      
      const matchesType = selectedTypes.length === 0 || 
        selectedTypes.includes(product.type);

      return matchesSearch && matchesManufacturer && matchesType;
    });
  }, [searchQuery, selectedManufacturers, selectedTypes]);

  const handleManufacturerChange = (manufacturer, checked) => {
    setSelectedManufacturers((prev) =>
      checked
        ? [...prev, manufacturer]
        : prev.filter((m) => m !== manufacturer)
    );
  };

  const handleTypeChange = (type, checked) => {
    setSelectedTypes((prev) =>
      checked
        ? [...prev, type]
        : prev.filter((t) => t !== type)
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedManufacturers([]);
    setSelectedTypes([]);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
              Products Catalog
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Products We Supply
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Explore our range of trusted ICT and security equipment from leading manufacturers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          {/* Note */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-8">
            <p className="text-sm text-foreground">
              <strong>Note:</strong> {productNote}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <FiltersSidebar
              manufacturers={manufacturers}
              productTypes={productTypes}
              selectedManufacturers={selectedManufacturers}
              selectedTypes={selectedTypes}
              searchQuery={searchQuery}
              onManufacturerChange={handleManufacturerChange}
              onTypeChange={handleTypeChange}
              onSearchChange={setSearchQuery}
              onClearFilters={clearAllFilters}
            />

            {/* Products Grid */}
            <div className="flex-1">
              {/* Active Filters */}
              <ActiveFilterChips
                selectedManufacturers={selectedManufacturers}
                selectedTypes={selectedTypes}
                searchQuery={searchQuery}
                onRemoveManufacturer={(m) => setSelectedManufacturers((prev) => prev.filter((x) => x !== m))}
                onRemoveType={(t) => setSelectedTypes((prev) => prev.filter((x) => x !== t))}
                onClearSearch={() => setSearchQuery('')}
                onClearAll={clearAllFilters}
              />

              {/* Results count */}
              <p className="text-sm text-muted-foreground mb-6">
                Showing {filteredProducts.length} of {products.length} products
              </p>

              {filteredProducts.length > 0 ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              ) : (
                <EmptyState onClearFilters={clearAllFilters} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Need Help Choosing?"
        description="Our team can help you select the right products for your specific requirements."
        primaryButtonText="Contact Us"
        primaryButtonLink="/contact"
      />
    </div>
  );
};

export default ProductsPage;
